import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LocationOption } from 'server/internal-services/public-bff.interface';
import { LocationMessageType } from 'types';
import { useFeatureFlags } from './FeatureFlagProvider';
import { Body, useLoading, useModal } from '@teko/ds-desktop';
import { isServerless } from 'helpers';
import {
  SESSION_STORAGE_LOCATION_ERROR_SHOWED,
  SESSION_STORAGE_LOCATION_HINT_SHOWED,
  SESSION_STORAGE_LOCATION_MESSAGE,
  userSelectedConstants,
} from '../constants';
import { t } from 'i18n';
import dynamic from 'next/dynamic';
import { useGlobalData } from './GlobalDataContext';
export interface LocationContextModel {
  locations: LocationOption[];
  isShowed?: boolean;
  setShowed?: (isShowed: boolean) => void;
  onSelectLocation?: (location: LocationOption) => void;
  showRegionPickerSuggestionPopover?: boolean;
  onCloseRegionPickerSuggestionPopover?: () => void;
  selectedLocation?: LocationOption;
  enabled?: boolean;
}

const LocationPicker = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.LocationPicker),
);

const LocationContext = createContext<LocationContextModel>({
  locations: [],
});

export interface LocationProviderProps {
  children: React.ReactNode;
}
export const useLocation = () => {
  return useContext(LocationContext);
};

export const LocationProvider: React.FC<LocationProviderProps> = ({
  children,
}) => {
  const {
    isMobile,
    location: selectedLocationCode,
    locations = [],
  } = useGlobalData();
  const [, setLoading] = useLoading('location');
  const [show, setShow] = useState<boolean>();
  const { flags } = useFeatureFlags();
  const { showModal } = useModal();

  const regionPickerSuggestionPopoverClosed =
    isServerless() &&
    sessionStorage?.getItem(SESSION_STORAGE_LOCATION_HINT_SHOWED);
  const showRegionPickerSuggestionPopover =
    !regionPickerSuggestionPopoverClosed &&
    isServerless() &&
    sessionStorage.getItem(SESSION_STORAGE_LOCATION_MESSAGE) ===
      LocationMessageType.Hint &&
    flags.epic_ecom_284;

  const onSelectLocation = (location: LocationOption) => {
    if (isServerless()) {
      localStorage.setItem(
        userSelectedConstants.LOCATION,
        location.locationCode,
      );
      setLoading(true);
      setShow(false);
      window.location.reload();
    }
  };

  const enabled = useMemo(() => {
    return isServerless() && locations.length > 1 && flags.epic_ecom_284;
  }, [locations]);

  useEffect(() => {
    if (
      isServerless() &&
      sessionStorage.getItem(SESSION_STORAGE_LOCATION_MESSAGE) ===
        LocationMessageType.Error &&
      // this condition prevents the popup showed multiple times
      !sessionStorage.getItem(SESSION_STORAGE_LOCATION_ERROR_SHOWED)
    ) {
      showModal({
        title: t('Thông báo'),
        children: (
          <Body level={1} style={{ textAlign: 'center' }}>
            {t(
              'Hiện tại không lấy được thông tin địa điểm để hiển thị sản phẩm phù hợp với bạn. Vui lòng thử lại hoặc quay lại sau giây lát!',
            )}
          </Body>
        ),
        onOk: () => {
          window.location.reload();
        },
        width: isMobile ? 300 : 400,
        okText: t('Thử lại'),
        alignFooter: 'center',
        centeredTitle: true,
        showCloseIcon: false,
      });
      sessionStorage.setItem(SESSION_STORAGE_LOCATION_ERROR_SHOWED, 'true');
    }
  }, []);

  const onCloseRegionPickerSuggestionPopover = () => {
    sessionStorage.setItem(SESSION_STORAGE_LOCATION_HINT_SHOWED, 'true');
  };

  const selectedLocation = useMemo(
    () =>
      locations.find(
        ({ locationCode }) => locationCode === selectedLocationCode,
      ),
    [locations, selectedLocationCode],
  );

  return (
    <LocationContext.Provider
      value={{
        locations,
        isShowed: show,
        setShowed: setShow,
        onSelectLocation,
        showRegionPickerSuggestionPopover,
        onCloseRegionPickerSuggestionPopover,
        selectedLocation,
        enabled,
      }}
    >
      {children}

      {!!show && (
        <LocationPicker
          isShowed={show}
          isMobile={isMobile}
          setShowed={setShow}
          locations={locations}
          onSelectLocation={onSelectLocation as never}
          selectedLocation={selectedLocation}
        />
      )}
    </LocationContext.Provider>
  );
};
